/**
 * Cyphex Teams — files, folders, versions, uploads, sharing.
 * Mirrors infra/sql/005_files.sql.
 */

import type {
  FileId,
  FolderId,
  IsoDateTime,
  JsonObject,
  ScopeKind,
  Timestamps,
  Uuid,
  UserId,
} from './common';
import type { UserRef } from './identity';

export type FileStatus =
  | 'pending'
  | 'uploading'
  | 'processing'
  | 'ready'
  | 'failed'
  | 'quarantined'
  | 'deleted';

/**
 * Drives the coloured glyph in the "Pinned files" card:
 * pdf=red, folder=yellow, doc=blue, notebook=purple, etc.
 */
export type FileClass =
  | 'pdf'
  | 'doc'
  | 'sheet'
  | 'slide'
  | 'image'
  | 'video'
  | 'audio'
  | 'archive'
  | 'code'
  | 'notebook'
  | 'folder'
  | 'other';

export type ShareAccess = 'view' | 'comment' | 'edit';

/** Compact file shape embedded in messages and lists. */
export interface FileRef {
  id: FileId;
  name: string;
  extension: string | null;
  mimeType: string;
  fileClass: FileClass;
  sizeBytes: number;
  status: FileStatus;
  thumbnailUrl: string | null;
  /** Short-lived presigned GET URL. Regenerated on each read. */
  downloadUrl: string | null;
}

export interface CyphexFile extends FileRef, Timestamps {
  scopeKind: ScopeKind;
  scopeId: Uuid;
  folderId: FolderId | null;
  /** Subtitle in the Pinned-files card ("Cyphex Technologies", "General"). */
  scopeLabel: string | null;
  previewUrl: string | null;
  versionCount: number;
  currentVersion: number;
  metadata: FileMetadata;
  isPinned: boolean;
  owner: UserRef | null;
  createdBy: UserRef | null;
  lastAccessedAt: IsoDateTime | null;
  downloadCount: number;
  legalHold: boolean;
  /** The caller's effective access. */
  access: ShareAccess | null;
}

export interface FileMetadata {
  width?: number;
  height?: number;
  durationMs?: number;
  pageCount?: number;
  codec?: string;
  /** Anything the preview worker extracted that has no first-class field. */
  extra?: JsonObject;
}

export interface Folder extends Timestamps {
  id: FolderId;
  parentId: FolderId | null;
  scopeKind: ScopeKind;
  scopeId: Uuid;
  name: string;
  path: string;
  itemCount?: number;
}

export interface FileVersion {
  id: Uuid;
  version: number;
  sizeBytes: number;
  checksum: string | null;
  mimeType: string | null;
  comment: string | null;
  createdBy: UserRef | null;
  createdAt: IsoDateTime;
  downloadUrl: string | null;
}

/* ---------------------------------------------------------------------------
 * Uploads
 *
 * FLOW (large + small use the same path so the client has one code path):
 *   1. POST /files/uploads          -> UploadSession with presigned part URLs
 *   2. PUT each part directly to S3 -> collect ETags
 *   3. POST /files/uploads/:id/complete { parts } -> CyphexFile
 * Bytes NEVER pass through apps/api.
 * ------------------------------------------------------------------------- */

/**
 * THE LARGEST FILE ONE UPLOAD MAY CARRY - 20 GiB, enforced by the API and read
 * by every client, so the composer and the server can never disagree. It used
 * to be 100 MB in the chat composer alone, while the API already took 20 GiB.
 * Bytes go straight to object storage in `partSize` parts (8 MiB), so the size
 * costs this box nothing.
 */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024 * 1024;

export interface CreateUploadRequest {
  filename: string;
  sizeBytes: number;
  mimeType?: string;
  scopeKind: ScopeKind;
  scopeId: Uuid;
  folderId?: FolderId;
  /** Replace an existing file, creating a new version instead of a new file. */
  replaceFileId?: FileId;
  /**
   * `attachment`: picked in a composer and not sent yet. The file is a DRAFT -
   * hidden from Files and search until a message carries it, and deleted by
   * the server if none ever does (infra/sql/074).
   */
  purpose?: 'attachment';
}

export interface UploadPart {
  partNumber: number;
  /** Presigned PUT URL, valid 1 hour. */
  url: string;
  /** Byte range this part covers, inclusive start / exclusive end. */
  rangeStart: number;
  rangeEnd: number;
}

export interface UploadSession {
  id: Uuid;
  fileId: FileId;
  partSize: number;
  parts: UploadPart[];
  expiresAt: IsoDateTime;
}

export interface CompleteUploadRequest {
  parts: Array<{ partNumber: number; etag: string }>;
}

/**
 * Fresh presigned URLs for parts whose URL has expired.
 *
 * A part URL is valid for one hour and a session for 24, so a large file on a
 * slow link outlives the URLs it was handed. The client asks again for the
 * parts it still has to send - POST /files/uploads/:id/parts.
 */
export interface RefreshUploadPartsRequest {
  partNumbers: number[];
}

export interface RefreshUploadPartsResponse {
  parts: Array<{ partNumber: number; url: string }>;
}

/* ---------------------------------------------------------------------------
 * Sharing & permissions
 * ------------------------------------------------------------------------- */

export type PrincipalKind = 'user' | 'group' | 'team' | 'channel';

export interface FilePermission {
  id: Uuid;
  principalKind: PrincipalKind;
  principalId: Uuid;
  principalLabel: string;
  access: ShareAccess;
  isDeny: boolean;
  expiresAt: IsoDateTime | null;
}

export interface ShareLink extends Timestamps {
  id: Uuid;
  fileId: FileId | null;
  folderId: FolderId | null;
  url: string;
  access: ShareAccess;
  audience: 'anyone' | 'tenant' | 'invited';
  hasPassword: boolean;
  allowDownload: boolean;
  maxUses: number | null;
  useCount: number;
  expiresAt: IsoDateTime | null;
  revokedAt: IsoDateTime | null;
  createdBy: UserRef;
}

export interface CreateShareLinkRequest {
  access?: ShareAccess;
  audience?: 'anyone' | 'tenant' | 'invited';
  password?: string;
  allowDownload?: boolean;
  maxUses?: number;
  expiresAt?: IsoDateTime;
}

export interface GrantFileAccessRequest {
  grants: Array<{
    principalKind: PrincipalKind;
    principalId: Uuid;
    access: ShareAccess;
    isDeny?: boolean;
    expiresAt?: IsoDateTime;
  }>;
  /** Send a notification + optional message to the grantees. */
  notify?: boolean;
  message?: string;
}

export interface FileListFilters {
  scopeKind?: ScopeKind;
  scopeId?: Uuid;
  folderId?: FolderId | null;
  fileClass?: FileClass[];
  ownerId?: UserId;
  query?: string;
  pinnedOnly?: boolean;
  sort?: 'name' | 'created' | 'modified' | 'size';
  direction?: 'asc' | 'desc';
}

/* ---------------------------------------------------------------------------
 * Redeeming a share link.
 *
 * The anonymous counterpart of `CreateShareLinkRequest`. It answers only what
 * a stranger holding the link may be told BEFORE they get the bytes: what the
 * file is, whether it may be downloaded, and whether a password stands in the
 * way. Never the owner, never the channel, never the workspace roster.
 * ------------------------------------------------------------------------- */

export interface SharedFilePreview {
  token: string;
  name: string;
  extension: string | null;
  mimeType: string;
  sizeBytes: number;
  fileClass: FileClass;
  access: ShareAccess;
  allowDownload: boolean;
  /** True when `POST /v1/share/:token/unlock` must be answered first. */
  requiresPassword: boolean;
  workspaceName: string;
  expiresAt: IsoDateTime | null;
}
